"use client";
import React, { FC, useCallback, useEffect } from "react";
import Cookies from "js-cookie";
import { X } from "react-feather";

import { useDisclosure } from "@mantine/hooks";
import { Modal } from "@mantine/core";
import Image from "next/image";
import HtmlText from "@/sections/common/HtmlText";
import TButton from "../atoms/buttons/TButton";
import { useQuery } from "@tanstack/react-query";
import { getImageUrl } from "@/utils/media.helper";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { informationPopUpQuery } from "@/api/information-pop-up";

import { getCookie, CookieName } from "@/utils/cookie.helper";

type ILatestInformationPopUp = {};

const LatestInformationPopUp: FC<ILatestInformationPopUp> = () => {
  const [opened, { open, close }] = useDisclosure(false);

  const enableQuery = useCallback(() => {
    return !!(
      getCookie(CookieName.MDT_CONSENT) && !getCookie(CookieName.LATEST_E_BOOK)
    );
  }, []);

  const { data, isError } = useQuery({
    ...informationPopUpQuery.getLatestInformationDetail(),
    enabled: enableQuery(),
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (enableQuery() && !isError) {
      timeout = setTimeout(() => {
        open();
      }, 60 * 1000); // 1 min
    }

    return () => clearTimeout(timeout);
  });

  const { heading, description, button, image } = data?.data.attributes ?? {};

  const onClose = () => {
    close();
    Cookies.set("latest-e-book-pop-up", "false", { expires: 365 });
    window.location.reload();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      classNames={{
        content: "rounded-none",
        body: "p-0",
        close: "size-3",
      }}
      centered
      size="xl"
      zIndex={9999}
      closeOnClickOutside={false}
      withCloseButton={false}
    >
      <div className="relative">
        <span
          className="absolute right-6 top-6 cursor-pointer"
          onClick={onClose}
        >
          <X className="size-6 text-black-950 lg:size-7" />
        </span>

        <div className="flex flex-col items-center justify-center gap-7 px-10 py-[60px] sm:px-20 md:flex-row md:gap-12 lg:gap-[60px] lg:px-[100px] lg:py-20">
          {image && (
            <Image
              src={getImageUrl(image)}
              alt={image.name}
              className="mx-auto !h-full w-44 object-cover lg:w-[216px]"
              height={298}
              width={216}
            />
          )}
          {(heading || description || button) && (
            <div className="flex flex-col items-center gap-4 md:items-start lg:gap-7">
              {heading && (
                <h5 className="text-center text-lg font-normal text-black-900 md:text-left lg:text-h6 xl:text-h5">
                  {heading}
                </h5>
              )}
              {description && (
                <HtmlText className="text-center md:text-left">
                  <BlocksRenderer content={description} />
                </HtmlText>
              )}
              {button && (
                <div onClick={onClose}>
                  <TButton
                    id={button.btn_id}
                    variant={button.variant}
                    target={button.target}
                    radius={button.radius}
                    href={button.href}
                  >
                    {button.title}
                  </TButton>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LatestInformationPopUp;
